import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Steps,
  Table,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import { buildWbsTableTree } from '@/utils/est3WbsTree';
import { layerLabel, type Est3WbsLayer, WBS_LAYER_HINTS } from '@/utils/est3WbsLayers';
import { Est3WbsLayerTag } from '@/components/epc/shared/Est3WbsLayerTag';

type DraftRow = {
  key: string;
  code: string;
  name: string;
  wbsLayerCode: string;
  parentCode?: string;
  description?: string;
  shortName?: string;
  sortOrder?: number;
};

export interface EpcWbsLibraryPackageGuidedWizardProps {
  open: boolean;
  onClose: () => void;
  packageId: string;
  existingNodes?: any[];
  onCompleted: () => void;
}

export const EpcWbsLibraryPackageGuidedWizard: React.FC<EpcWbsLibraryPackageGuidedWizardProps> = ({
  open,
  onClose,
  packageId,
  existingNodes = [],
  onCompleted,
}) => {
  const [step, setStep] = useState(0);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [nodeForm] = Form.useForm();

  const layersQ = useQuery({
    queryKey: ['epc-wbs-layers'],
    queryFn: () => epcService.getWbsLayers(),
    enabled: open,
  });
  const layers = (layersQ.data || []) as Est3WbsLayer[];

  const previewTree = useMemo(() => {
    const merged = [
      ...existingNodes.map((n) => ({
        id: String(n.id),
        code: n.code,
        name: n.name,
        parent_id: n.parent_id,
        layer_code: n.layer_code,
      })),
      ...drafts.map((d, i) => ({
        id: `draft-${i}`,
        code: d.code,
        name: d.name,
        parent_id: d.parentCode
          ? existingNodes.find((n) => n.code === d.parentCode)?.id ||
            drafts.find((x) => x.code === d.parentCode)?.key
          : null,
        layer_code: d.wbsLayerCode,
      })),
    ];
    return buildWbsTableTree(merged as any);
  }, [drafts, existingNodes]);

  const commitM = useMutation({
    mutationFn: async () => {
      const codeToId = new Map<string, string>();
      for (const n of existingNodes) codeToId.set(String(n.code), String(n.id));
      const sorted = [...drafts].sort((a, b) => {
        const da = a.parentCode ? 1 : 0;
        const db = b.parentCode ? 1 : 0;
        return da - db;
      });
      for (const d of sorted) {
        const parentId = d.parentCode ? codeToId.get(d.parentCode) || null : null;
        const created = (await epcWbsLibraryService.createPackageNode(packageId, {
          code: d.code,
          name: d.name,
          parentId,
          wbsLayerCode: d.wbsLayerCode,
          description: d.description,
          shortName: d.shortName,
          sortOrder: d.sortOrder,
        })) as any;
        codeToId.set(d.code, String(created.id));
      }
    },
    onSuccess: () => {
      message.success('WBS structure created');
      setDrafts([]);
      setStep(0);
      onCompleted();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const addDraft = (v: Record<string, unknown>) => {
    const row: DraftRow = {
      key: `d-${Date.now()}`,
      code: String(v.code),
      name: String(v.name),
      wbsLayerCode: String(v.wbsLayerCode),
      parentCode: v.parentCode ? String(v.parentCode) : undefined,
      description: v.description ? String(v.description) : undefined,
      shortName: v.shortName ? String(v.shortName) : undefined,
      sortOrder: v.sortOrder != null ? Number(v.sortOrder) : 100,
    };
    setDrafts((prev) => [...prev, row]);
    nodeForm.resetFields();
  };

  const parentOptions = useMemo(() => {
    const opts = [
      ...existingNodes.map((n) => ({ value: n.code, label: `${n.code} — ${n.name}` })),
      ...drafts.map((d) => ({ value: d.code, label: `${d.code} — ${d.name}` })),
    ];
    return opts;
  }, [existingNodes, drafts]);

  return (
    <Modal
      title="Guided package WBS setup"
      open={open}
      onCancel={onClose}
      width={720}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          {step > 0 ? <Button onClick={() => setStep((s) => s - 1)}>Back</Button> : null}
          {step < 2 ? (
            <Button type="primary" onClick={() => setStep((s) => s + 1)}>
              Next
            </Button>
          ) : (
            <Button
              type="primary"
              loading={commitM.isPending}
              disabled={drafts.length === 0}
              onClick={() => commitM.mutate()}
            >
              Create {drafts.length} node(s)
            </Button>
          )}
        </Space>
      }
      destroyOnClose
    >
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 16 }}
        items={[
          { title: 'Layers' },
          { title: 'Add nodes' },
          { title: 'Preview' },
        ]}
      />

      {step === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Build PROJECT → UNIT → AREA → BLOCK → MODULE → PACKAGE hierarchy"
          description="Each package should reach at least one PACKAGE (CWP) node for CBS cost templates."
        />
      ) : null}

      {step === 1 ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Form form={nodeForm} layout="inline" onFinish={addDraft}>
            <Form.Item name="code" rules={[{ required: true }]}>
              <Input placeholder="Code" style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="name" rules={[{ required: true }]}>
              <Input placeholder="Name" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="wbsLayerCode" rules={[{ required: true }]}>
              <Select
                placeholder="Layer"
                style={{ width: 130 }}
                options={layers.map((l) => ({ value: l.code, label: l.code }))}
              />
            </Form.Item>
            <Form.Item name="parentCode">
              <Select allowClear placeholder="Parent" style={{ width: 140 }} options={parentOptions} />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit" icon={<PlusOutlined />}>
                Add
              </Button>
            </Form.Item>
          </Form>
          <Table
            size="small"
            rowKey="key"
            dataSource={drafts}
            pagination={false}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 90 },
              { title: 'Name', dataIndex: 'name' },
              {
                title: 'Layer',
                dataIndex: 'wbsLayerCode',
                width: 100,
                render: (v) => <Est3WbsLayerTag layerCode={v} />,
              },
              { title: 'Parent', dataIndex: 'parentCode', width: 90 },
            ]}
          />
          {layers.map((l) => (
            <div key={l.code} style={{ fontSize: 12, color: '#666' }}>
              <Est3WbsLayerTag layerCode={l.code} /> {WBS_LAYER_HINTS[l.code] || layerLabel(l)}
            </div>
          ))}
        </Space>
      ) : null}

      {step === 2 ? (
        <Table
          size="small"
          rowKey="id"
          dataSource={previewTree}
          pagination={false}
          expandable={{ defaultExpandAllRows: true }}
          columns={[
            { title: 'Code', dataIndex: 'code', width: 110 },
            { title: 'Name', dataIndex: 'name' },
            {
              title: 'Layer',
              dataIndex: 'layer_code',
              width: 100,
              render: (v) => <Est3WbsLayerTag layerCode={v} />,
            },
          ]}
        />
      ) : null}
    </Modal>
  );
};
